import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Ray Tag Cross-sell
 *
 * Cart-only add-on block (snippets/ray-tag-cross-sell.liquid) - suggests products tagged
 * `ray-sell-addon` that share a `ray-sell-pair:<id>` tag with something already in the cart, and
 * once BOTH sides of a pair are in the cart, attaches a line item property
 * (`_ray_sell_discount_id`, value = the addon's product handle) to the addon's cart line. A
 * third-party discount app (e.g. Discounts Hub "line item property" discount) watches that
 * property and applies the real discount at checkout - no Shopify Function, no duplicate
 * product/variant, inventory stays a single pool.
 *
 * Each recommendation card can carry real variant options (color swatches / size select, same
 * mechanic as assets/ray-cart-cross-sell.js) - the option-sync logic below just keeps the
 * card's price/image/variant id in step with the shopper's picks; picking a size doesn't
 * itself touch the cart.
 *
 * Line item properties can't be edited on an existing cart line - the same remove+re-add swap
 * pattern as assets/ray-gift-wrap.js is used here, just swapping properties instead of variants.
 */
const SOURCE_ID = 'ray-tag-cross-sell';

// The discount app rule (Discounts Hub) matches purely on this property being present - the
// value itself is never read by it, so every addon uses the same flat discount. Only the
// property's presence drives hasDiscount below.
const DISCOUNT_PROPERTIES = { _ray_sell_discount_id: 'true' };

const selectors = {
  cartData: '[data-ray-xsell-cart-data]',
  card: '[data-ray-xsell-card]',
  form: '[data-ray-xsell-form]',
  variantsJson: '[data-ray-xsell-variants]',
  optionControl: '[data-ray-xsell-option]',
  fallbackSelect: '[data-ray-xsell-fallback-select]',
  variantIdInput: '[data-ray-xsell-variant-id]',
  price: '[data-ray-xsell-price]',
  comparePrice: '[data-ray-xsell-compare-price]',
  image: '.ray-tag-cross-sell__image',
};

let isSyncing = false;

const cartRequest = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Ray tag cross-sell: ${url} failed (${response.status})`);
  }

  return response.json();
};

const addVariant = (variantId, properties, quantity = 1) =>
  cartRequest('/cart/add.js', {
    items: [
      {
        id: Number(variantId),
        quantity,
        ...(properties ? { properties } : {}),
      },
    ],
  });

const setLineProperties = (lineKey, quantity, properties) =>
  cartRequest('/cart/change.js', { id: lineKey, quantity, properties });

const fetchCart = async () => {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });

  if (!response.ok) {
    throw new Error(`Ray tag cross-sell: /cart.js failed (${response.status})`);
  }

  return response.json();
};

const announceCartUpdate = (cart) => {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, {
      source: SOURCE_ID,
      itemCount: cart.item_count,
    })
  );
};

/**
 * @returns {{cartPairIds: string[], addonLines: Array<{variantId: number, lineKey: string, pairIds: string[], hasDiscount: boolean, quantity: number}>} | null}
 */
const readCartData = () => {
  const node = document.querySelector(selectors.cartData);

  if (!node?.textContent) return null;

  try {
    return JSON.parse(node.textContent.trim());
  } catch (error) {
    console.error('Ray tag cross-sell: cart data JSON parse failed', error);
    return null;
  }
};

const getVariants = (card) => {
  const json = card.querySelector(selectors.variantsJson);

  if (!json) return [];

  try {
    return JSON.parse(json.textContent.trim());
  } catch (error) {
    console.error('Ray tag cross-sell: variants JSON parse failed', error);
    return [];
  }
};

const getSelectedOptions = (card) => {
  const selectedOptions = {};

  card.querySelectorAll(selectors.optionControl).forEach((control) => {
    const position = control.dataset.optionPosition;

    if (!position) return;
    if (control.type === 'radio' && !control.checked) return;

    selectedOptions[`option${position}`] = control.value;
  });

  return selectedOptions;
};

const variantMatchesOptions = (variant, selectedOptions) =>
  Object.entries(selectedOptions).every(([key, value]) => !value || variant[key] === value);

const findVariant = (card) => {
  const variants = getVariants(card);
  const fallbackSelect = card.querySelector(selectors.fallbackSelect);

  if (fallbackSelect) {
    const selectedId = Number(fallbackSelect.value);
    return variants.find((variant) => Number(variant.id) === selectedId) || variants.find((variant) => variant.available);
  }

  const selectedOptions = getSelectedOptions(card);

  return (
    variants.find((variant) => variant.available && variantMatchesOptions(variant, selectedOptions)) ||
    variants.find((variant) => variantMatchesOptions(variant, selectedOptions)) ||
    variants.find((variant) => variant.available) ||
    variants[0]
  );
};

const updateSizeAvailability = (card) => {
  const variants = getVariants(card);
  const selectedOptions = getSelectedOptions(card);
  const sizeSelect = Array.from(card.querySelectorAll(`select${selectors.optionControl}`)).find(
    (select) => select.dataset.optionPosition
  );

  if (!sizeSelect) return;

  const sizePosition = sizeSelect.dataset.optionPosition;
  const optionsWithoutSize = { ...selectedOptions };

  delete optionsWithoutSize[`option${sizePosition}`];

  Array.from(sizeSelect.options).forEach((option) => {
    const testOptions = { ...optionsWithoutSize, [`option${sizePosition}`]: option.value };
    const hasAvailableVariant = variants.some((variant) => variant.available && variantMatchesOptions(variant, testOptions));

    option.disabled = !hasAvailableVariant;
  });

  if (sizeSelect.selectedOptions[0]?.disabled) {
    const firstAvailableOption = Array.from(sizeSelect.options).find((option) => !option.disabled);

    if (firstAvailableOption) sizeSelect.value = firstAvailableOption.value;
  }
};

const updateCard = (card) => {
  updateSizeAvailability(card);

  const variant = findVariant(card);

  if (!variant) return;

  const variantIdInput = card.querySelector(selectors.variantIdInput);
  const priceElement = card.querySelector(selectors.price);
  const comparePriceElement = card.querySelector(selectors.comparePrice);
  const imageElement = card.querySelector(selectors.image);
  const button = card.querySelector('button[type="submit"]');

  if (variantIdInput) variantIdInput.value = variant.id;
  if (priceElement && variant.price) priceElement.textContent = variant.price;

  if (comparePriceElement) {
    if (variant.compareAtPrice) {
      comparePriceElement.textContent = variant.compareAtPrice;
      comparePriceElement.hidden = false;
    } else {
      comparePriceElement.textContent = '';
      comparePriceElement.hidden = true;
    }
  }

  if (imageElement && variant.image) {
    imageElement.src = variant.image;
    imageElement.srcset = '';
  }

  if (button) button.disabled = !variant.available;
};

const initCards = (root = document) => {
  root.querySelectorAll(selectors.card).forEach((card) => updateCard(card));
};

const handleFormSubmit = async (event) => {
  const form = event.target.closest(selectors.form);

  if (!form) return;

  event.preventDefault();

  const variantId = form.querySelector(selectors.variantIdInput)?.value;
  const button = form.querySelector('button[type="submit"]');

  if (!variantId) return;

  if (button) button.disabled = true;

  try {
    await addVariant(variantId, DISCOUNT_PROPERTIES);

    const cart = await fetchCart();

    announceCartUpdate(cart);
  } catch (error) {
    console.error('Ray tag cross-sell: failed to add product', error);
  } finally {
    if (button) button.disabled = false;
  }
};

// ---- Reconciliation for addon lines already in the cart ----

/**
 * Re-checks every addon line already in the cart against the current pair-id set, and
 * attaches/removes the discount property line as needed.
 * @param {ReturnType<typeof readCartData>} data
 */
const reconcile = async (data) => {
  if (isSyncing || !data) return;

  const { cartPairIds, addonLines } = data;

  if (!Array.isArray(addonLines) || addonLines.length === 0) return;

  const pairIdSet = new Set(cartPairIds || []);

  const mismatchedLines = addonLines.filter((line) => {
    const shouldHaveDiscount = (line.pairIds || []).some((id) => pairIdSet.has(id));

    return shouldHaveDiscount !== line.hasDiscount;
  });

  if (mismatchedLines.length === 0) return;

  isSyncing = true;

  try {
    let freshCart = null;

    for (const line of mismatchedLines) {
      const shouldHaveDiscount = (line.pairIds || []).some((id) => pairIdSet.has(id));

      try {
        const liveCart = await fetchCart();
        const liveLine = liveCart.items.find((item) => {
          if (item.variant_id !== line.variantId) return false;

          return Boolean(item.properties?._ray_sell_discount_id) !== shouldHaveDiscount;
        });

        if (!liveLine) continue;

        const properties = { ...(liveLine.properties || {}) };

        if (shouldHaveDiscount) {
          Object.assign(properties, DISCOUNT_PROPERTIES);
        } else {
          properties._ray_sell_discount_id = '';
        }

        freshCart = await setLineProperties(liveLine.key, liveLine.quantity, properties);
      } catch (error) {
        console.error('Ray tag cross-sell: failed to sync one line', error);
      }
    }

    if (freshCart) announceCartUpdate(freshCart);
  } catch (error) {
    console.error('Ray tag cross-sell: failed to sync discount property', error);
  } finally {
    isSyncing = false;
  }
};

document.addEventListener('change', (event) => {
  const optionControl = event.target.closest(selectors.optionControl);
  const fallbackSelect = event.target.closest(selectors.fallbackSelect);

  if (!optionControl && !fallbackSelect) return;

  const card = event.target.closest(selectors.card);

  if (card) updateCard(card);
});

document.addEventListener('submit', handleFormSubmit);

let lastCartDataText = document.querySelector(selectors.cartData)?.textContent?.trim() ?? null;
let pendingSourceId = null;

const checkForFreshData = () => {
  const currentText = document.querySelector(selectors.cartData)?.textContent?.trim() ?? null;

  if (currentText === lastCartDataText) return;

  lastCartDataText = currentText;
  initCards();

  const sourceId = pendingSourceId;

  pendingSourceId = null;

  if (sourceId === SOURCE_ID) return;

  reconcile(readCartData());
};

new MutationObserver(checkForFreshData).observe(document.body, {
  childList: true,
  subtree: true,
  characterData: true,
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId) pendingSourceId = event.detail.sourceId;
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCards());
} else {
  initCards();
}
